import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { detectSensitiveInfo, SensitiveDataResult } from '../../utils/sensitiveData';
import SensitiveInfoModal from '../Modals/SensitiveInfoModal';
import { FileRecord } from '../../types';
import { useSocket } from '../../context/SocketContext';
import { API_BASE, getAbsoluteUrl } from '../../api/client';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import PlaceholderExtension from '@tiptap/extension-placeholder';
import LinkExtension from '@tiptap/extension-link';
import UnderlineExtension from '@tiptap/extension-underline';
import { Markdown as TiptapMarkdown } from 'tiptap-markdown';
import {
    Lock,
    Unlock,
    Send,
    Bold as BoldIcon,
    Italic as ItalicIcon,
    Underline as UnderlineIcon,
    Link2 as LinkIcon,
    List,
    ListOrdered,
    Code as CodeIcon,
    Quote as QuoteIcon,
    Paperclip,
    Smile,
    Bomb,
    X,
    Image as ImageIcon,
    Loader2,
    Clock,
    ChevronDown,
    File as FileIcon,
    Keyboard,
    Calendar,
    Sun
} from 'lucide-react';

type ComposerDraft = {
    content: string;
    attachments: FileRecord[];
    isEncrypted: boolean;
    selfDestruct: boolean;
    scheduledTime: string | null;
};

const conversationDrafts = new Map<string, ComposerDraft>();

export default function MessageComposer({
    draftKey,
    placeholder = 'Type a message...',
    onSend,
    isThread = false,
    initialContent = '',
    initialAttachments = [],
    initialEncryption = null,
    onCancel,
    submitLabel = null,
    isEditing = false
}: {
    draftKey?: string,
    placeholder?: string,
    onSend?: any,
    isThread?: boolean,
    initialContent?: string,
    initialAttachments?: any[],
    initialEncryption?: boolean | null,
    onCancel?: () => void,
    submitLabel?: string | null,
    isEditing?: boolean
}) {
    const savedDraft = draftKey ? conversationDrafts.get(draftKey) : undefined;
    const [content, setContent] = useState(savedDraft?.content ?? initialContent);
    const [isEncrypted, setIsEncrypted] = useState(savedDraft?.isEncrypted ?? false);
    const [selfDestruct, setSelfDestruct] = useState(savedDraft?.selfDestruct ?? false);
    const [isBroadcast, setIsBroadcast] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);

    // Mentions state
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionIndex, setMentionIndex] = useState(0); // For keyboard navigation

    // Attachments & Emoji
    const [attachments, setAttachments] = useState<FileRecord[]>(savedDraft?.attachments ?? initialAttachments);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showEmoji, setShowEmoji] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showSchedule, setShowSchedule] = useState(false);
    const [scheduledTime, setScheduledTime] = useState<string | null>(savedDraft?.scheduledTime ?? null);
    const [customDateValue, setCustomDateValue] = useState('');

    // Drag-and-drop
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    // File Retention
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [retentionDays, setRetentionDays] = useState<number | null>(null);
    const [neverDelete, setNeverDelete] = useState(false);
    const [disableCompression, setDisableCompression] = useState(false);

    // Sensitive Data State
    const [sensitiveModalState, setSensitiveModalState] = useState<{
        isOpen: boolean;
        detectedType: SensitiveDataResult['type'];
    }>({ isOpen: false, detectedType: null });
    const [isSendingDelayed, setIsSendingDelayed] = useState(false);
    const delayedSendTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const delayedSendDataRef = useRef<{ encrypt: boolean, selfDestruct: boolean, content: string, attachments: any[], scheduledTime: any, broadcast?: boolean } | null>(null);

    const { sendMessage, replyToThread, activeView, channels, topics, currentUser, users, settings, uploadFile, updateFileRetention, messages, setEditingMessageId, setEditContent, isOnline, authToken } = useApp();
    const { showToast } = useToast();
    const { sendTyping, stopTyping } = useSocket();
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const emojiRef = useRef<HTMLDivElement>(null);
    const shortcutsRef = useRef<HTMLDivElement>(null);
    const scheduleRef = useRef<HTMLDivElement>(null);

    // Close popovers on outside click or Escape
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (emojiRef.current && !emojiRef.current.contains(event.target as Node)) {
                setShowEmoji(false);
            }
            if (shortcutsRef.current && !shortcutsRef.current.contains(event.target as Node)) {
                setShowShortcuts(false);
            }
            if (scheduleRef.current && !scheduleRef.current.contains(event.target as Node)) {
                setShowSchedule(false);
            }
        };

        const handleGlobalKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowEmoji(false);
                setShowShortcuts(false);
                setShowSchedule(false);
                setShowMentions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, []);

    // Determine container type for typing events
    const getContainerInfo = useCallback(() => {
        const type = activeView.type as 'channel' | 'dm' | 'topic';
        return { id: activeView.id, type };
    }, [activeView.id, activeView.type]);

    // Determine the encryption mode for the current view
    const getEncryptionMode = () => {
        if (activeView.type === 'channel') {
            const channel = channels.find(c => c.id === activeView.id);
            return channel?.encryption_mode || 'optional';
        } else if (activeView.type === 'dm') {
            return settings?.encryption_policy?.dm_mode || 'optional';
        } else if (activeView.type === 'topic') {
            // Topics are top-level — look them up from the topics state directly
            const topic = topics.find(t => t.id === activeView.id);
            if (topic) return topic.encryption_mode || 'optional';
        }
        return 'optional';
    };

    const encryptionMode = getEncryptionMode();
    const isForced = encryptionMode === 'forced';
    const isDisabled = encryptionMode === 'disabled';

    // Auto-set encryption based on mode and initial state
    useEffect(() => {
        if (initialEncryption !== null) {
            setIsEncrypted(initialEncryption);
        } else if (isForced) {
            setIsEncrypted(true);
        } else if (isDisabled) {
            setIsEncrypted(false);
        } else {
            setIsEncrypted(savedDraft?.isEncrypted ?? false);
        }
    }, [isForced, isDisabled, activeView.id, initialEncryption, draftKey]);

    // Determine the effective encryption state
    const effectiveEncryption = isForced ? true : (isDisabled ? false : isEncrypted);

    // TipTap Editor Initialization
    const editor = useEditor({
        extensions: [
            StarterKit,
            PlaceholderExtension.configure({
                placeholder: () => !isOnline ? 'You are offline' : (effectiveEncryption ? 'Type a secure message...' : placeholder),
            }),
            LinkExtension.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'editor-link',
                },
            }),
            UnderlineExtension,
            TiptapMarkdown,
        ],
        content: savedDraft?.content ?? initialContent,
        onUpdate: ({ editor }) => {
            // @ts-ignore - tiptap-markdown injects this storage but type definitions are missing
            const newContent = editor.storage.markdown.getMarkdown();
            setContent(newContent);
            handleInputLogic(newContent);
        },
        editorProps: {
            attributes: {
                class: 'composer-input prose-editor',
            },
            handlePaste: (_, event) => {
                handlePaste(event);
                return false; // let tip tap do the text pasting
            },
            handleKeyDown: (_, event) => {
                return handleKeyDownLogic(event);
            }
        }
    });

    useEffect(() => {
        // @ts-ignore
        if (editor && initialContent && initialContent !== editor.storage.markdown.getMarkdown()) {
            editor.commands.setContent(initialContent);
        }
    }, [initialContent, editor]);

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(isOnline);
    }, [editor, isOnline]);

    // Keep drafts in memory and scope them to one conversation.
    useEffect(() => {
        if (!draftKey) return;

        const hasDraft = Boolean(content.trim() || attachments.length > 0 || scheduledTime || selfDestruct);
        if (!hasDraft) {
            conversationDrafts.delete(draftKey);
            return;
        }

        conversationDrafts.set(draftKey, {
            content,
            attachments: [...attachments],
            isEncrypted,
            selfDestruct,
            scheduledTime,
        });
    }, [attachments, content, draftKey, isEncrypted, scheduledTime, selfDestruct]);

    const formatBold = () => editor?.chain().focus().toggleBold().run();
    const formatItalic = () => editor?.chain().focus().toggleItalic().run();
    const formatUnderline = () => editor?.chain().focus().toggleUnderline().run();
    const formatLink = () => {
        const url = window.prompt('Enter link URL:');
        if (url) {
            editor?.chain().focus().setLink({ href: url }).run();
        }
    };
    const formatBulletList = () => editor?.chain().focus().toggleBulletList().run();
    const formatNumberList = () => editor?.chain().focus().toggleOrderedList().run();
    const formatCode = () => {
        if (!editor) return;
        if (editor.state.selection.empty) {
            editor.chain().focus().toggleCode().run();
        } else {
            editor.chain().focus().toggleCodeBlock().run();
        }
    };
    const formatQuote = () => editor?.chain().focus().toggleBlockquote().run();


    // Can we use encryption? Channels, DMs, and topics all support E2EE
    const canEncrypt = activeView.type === 'channel' || activeView.type === 'dm' || activeView.type === 'topic';

    const executeSend = async (finalContent: string, finalEncryption: boolean, finalAttachments: FileRecord[], finalSelfDestruct: boolean, finalScheduledTime: string | null, finalBroadcast: boolean = false) => {
        const options = (finalEncryption && finalSelfDestruct) || finalAttachments.length > 0 || finalScheduledTime ? {
            selfDestruct: finalEncryption && finalSelfDestruct,
            duration: currentUser?.preferences?.self_destruct_timer || 300,
            attachments: finalAttachments,
            scheduledTime: finalScheduledTime
        } : undefined;

        if (onSend) {
            await onSend(finalContent, finalEncryption, finalAttachments, { selfDestruct: finalSelfDestruct, scheduledTime: finalScheduledTime });
        } else if (isThread) {
            await replyToThread(finalContent, finalEncryption, { attachments: finalAttachments, broadcast: finalBroadcast });
        } else {
            await sendMessage(finalContent, finalEncryption, options);
        }

        // Stop typing when message is sent
        const { id, type } = getContainerInfo();
        if (type === 'channel' || type === 'dm' || type === 'topic') {
            stopTyping(id, type);
        }
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };

    const startDelayedSend = (encrypt: boolean, selfDestructOption: boolean) => {
        const sendDelayPref = currentUser?.preferences?.send_delay;
        const delayMs = sendDelayPref === null ? 0 : (sendDelayPref ?? 3) * 1000;

        // If delay is disabled (null), execute immediately without UI countdown
        if (delayMs === 0) {
            executeSend(content, encrypt, [...attachments], selfDestructOption, scheduledTime, isBroadcast)
                .catch(err => {
                    console.error("Send error:", err);
                    showToast('Send failed: ' + err.message, 'error');
                });
            setContent('');
            editor?.commands.setContent('');
            setAttachments([]);
            setPendingFiles([]);
            setRetentionDays(null);
            setNeverDelete(false);
            setDisableCompression(false);
            setScheduledTime(null);
            if (getEncryptionMode() !== 'forced') setIsEncrypted(false);
            if (isEditing) { setEditingMessageId(null); setEditContent(''); }
            if (onCancel) onCancel();
            setTimeout(() => { editor?.commands.focus(); }, 0);
            return;
        }

        setIsSendingDelayed(true);
        delayedSendDataRef.current = {
            encrypt,
            selfDestruct: selfDestructOption,
            content: content,
            attachments: [...attachments],
            scheduledTime: scheduledTime,
            broadcast: isBroadcast
        };

        // Instantly clear the composer UI so the user thinks it's sent
        if (!onSend) {
            setContent('');
            editor?.commands.clearContent();
            setAttachments([]);
            setSelfDestruct(false);
            setScheduledTime(null);

            if (getEncryptionMode() !== 'forced') {
                setIsEncrypted(false);
            }
        }

        delayedSendTimeoutRef.current = setTimeout(() => {
            if (delayedSendDataRef.current) {
                // Actually execute the internal send using the original values that were captured
                executeSend(
                    delayedSendDataRef.current.content,
                    delayedSendDataRef.current.encrypt,
                    delayedSendDataRef.current.attachments,
                    delayedSendDataRef.current.selfDestruct,
                    delayedSendDataRef.current.scheduledTime,
                    delayedSendDataRef.current.broadcast || false
                ).catch(err => {
                    console.error("Delayed send error:", err);
                    showToast('Send failed: ' + err.message, 'error');
                });

                setContent('');
                editor?.commands.setContent('');
                setAttachments([]);
                setPendingFiles([]);
                setRetentionDays(null);
                setNeverDelete(false);
                setDisableCompression(false);
                setScheduledTime(null);
                setIsSendingDelayed(false);
                if (delayedSendTimeoutRef.current) {
                    clearTimeout(delayedSendTimeoutRef.current);
                    delayedSendTimeoutRef.current = null;
                }
                delayedSendDataRef.current = null;
                if (isEditing) {
                    setEditingMessageId(null);
                    setEditContent('');
                }
                if (onCancel) onCancel();
                setTimeout(() => {
                    editor?.commands.focus();
                }, 0);
            }
        }, delayMs);
    };


    const cancelDelayedSend = () => {
        if (delayedSendTimeoutRef.current) {
            clearTimeout(delayedSendTimeoutRef.current);
            delayedSendTimeoutRef.current = null;
        }

        // Restore the input values so the user can continue editing
        if (delayedSendDataRef.current && !onSend) {
            setContent(delayedSendDataRef.current.content);
            editor?.commands.setContent(delayedSendDataRef.current.content);
            setAttachments(delayedSendDataRef.current.attachments);
            setSelfDestruct(delayedSendDataRef.current.selfDestruct);
            setScheduledTime(delayedSendDataRef.current.scheduledTime);
        }

        setIsSendingDelayed(false);
        delayedSendDataRef.current = null;
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (!content.trim() && attachments.length === 0) return;
        if (isSendingDelayed) return;

        // Check for sensitive data before sending
        const sensitiveCheck = detectSensitiveInfo(content, settings);
        if (sensitiveCheck.detected) {
            if (effectiveEncryption && selfDestruct) {
                // Both are already enabled, bypass popup and send
                startDelayedSend(effectiveEncryption, selfDestruct);
                return;
            }

            setSensitiveModalState({
                isOpen: true,
                detectedType: sensitiveCheck.type
            });
            return; // Pause send
        }

        startDelayedSend(effectiveEncryption, selfDestruct);
    };

    // Shared upload logic — used by both the file picker and drag-and-drop
    const uploadFiles = async (files: File[]) => {
        if (!files.length) return;

        const maxMb = settings?.max_upload_size_mb || settings?.max_file_size_mb || 10;

        // Validate first
        for (const file of files) {
            if (file.size > maxMb * 1024 * 1024) {
                alert(`File ${file.name} exceeds limit of ${maxMb}MB`);
                return;
            }
        }

        // If retention is enabled, show warning and store pending files
        if (settings?.file_retention_enabled && settings?.show_retention_warning) {
            const thresholdBytes = (settings.file_size_threshold_mb || 5) * 1024 * 1024;
            const hasLargeFile = files.some(f => f.size >= thresholdBytes);

            if (hasLargeFile) {
                setPendingFiles(files);

                // Calculate default retention for first file
                const isLarge = files[0].size >= thresholdBytes;
                const defaultRetention = isLarge
                    ? settings.large_file_retention_days
                    : settings.small_file_retention_days;

                setRetentionDays(defaultRetention || 30);
                setNeverDelete(false);
                setDisableCompression(false);
                return; // Wait for user to confirm via popup
            }
        }

        // No retention warning (or all small files), upload immediately
        setIsUploading(true);
        try {
            for (const file of files) {
                const uploaded = await uploadFile(file);
                setAttachments(prev => [...prev, uploaded]);
            }
        } catch (error) {
            console.error(error);
            alert('Failed to upload file.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleFileSelect = async (e: any) => {
        const files = Array.from(e.target.files as FileList);
        await uploadFiles(files);
    };

    // Drag-and-drop handlers
    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        if (!isOnline) return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            await uploadFiles(files);
        }
    };

    const handleRetentionUpdate = async (fileId: string, neverDelete: boolean) => {
        // Optimistic local update
        const originalAttachments = [...attachments];
        setAttachments(prev => prev.map(a => {
            const currentId = (a as any).id || (a as any).file_id;
            return currentId === fileId ? { ...a, never_delete: neverDelete } : a;
        }));

        try {
            const updatedFile = await updateFileRetention(fileId, {
                never_delete: neverDelete,
                // If never delete is true, retention is infinite (0)
                retention_days: neverDelete ? 0 : settings?.small_file_retention_days || 30
            });
            console.log('[Retention] Update success:', updatedFile);

            setAttachments(prev => prev.map(a => {
                const currentId = (a as any).id || (a as any).file_id;
                const resId = (updatedFile as any).id || (updatedFile as any).file_id;
                return currentId === resId ? { ...a, ...updatedFile } : a;
            }));
        } catch (err) {
            console.error('Failed to update retention:', err);
            setAttachments(originalAttachments);
        }
    };

    const confirmUpload = async () => {
        if (!pendingFiles.length) return;

        setIsUploading(true);
        try {
            for (const file of pendingFiles) {
                const formData = new FormData();
                formData.append('file', file);

                // Add retention preferences
                if (retentionDays !== null) {
                    formData.append('retention_days', retentionDays.toString());
                }
                if (neverDelete) {
                    formData.append('never_delete', 'true');
                }
                if (disableCompression) {
                    formData.append('disable_compression', 'true');
                }

                // Upload with preferences
                const response = await fetch(`${API_BASE}/upload`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
                    },
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error(`Upload failed: ${response.statusText}`);
                }

                const uploaded = await response.json();
                setAttachments(prev => [...prev, uploaded]);
            }
        } catch (error) {
            console.error(error);
            alert('Failed to upload file.');
        } finally {
            setIsUploading(false);
            setPendingFiles([]);
            setRetentionDays(null);
            setNeverDelete(false);
            setDisableCompression(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const cancelUpload = () => {
        setPendingFiles([]);
        setRetentionDays(null);
        setNeverDelete(false);
        setDisableCompression(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };


    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const addEmoji = (emoji: string) => {
        editor?.chain().focus().insertContent(emoji).run();
        setShowEmoji(false);
    };


    const handleKeyDownLogic = (e: any) => {
        if (e.isComposing || e.keyCode === 229) return false;

        if (showMentions) {
            const filteredUsers = users.filter((u: any) => u.name.toLowerCase().includes(mentionQuery.toLowerCase()));
            if (filteredUsers.length === 0 && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(e.key)) {
                e.preventDefault();
                return true;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex(prev => (prev + 1) % filteredUsers.length);
                return true;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
                return true;
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                if (filteredUsers[mentionIndex]) {
                    selectMention(filteredUsers[mentionIndex]);
                }
                return true;
            } else if (e.key === 'Escape') {
                setShowMentions(false);
                return true;
            } else if (e.key === ' ' || e.key === 'Spacebar') {
                setShowMentions(false);
                return false; // let the space type normally!
            }
            // For letters, numbers, etc while menu is open:
            return false;
        }

        if (e.key === 'ArrowUp' && !content.trim() && !showMentions) {
            e.preventDefault();
            // Find last message by current user in this context
            const lastSent = [...messages].reverse().find(m => m.author_id === currentUser?.id && !m.is_deleted);
            if (lastSent) {
                setEditingMessageId(lastSent.id);
                setEditContent(lastSent.content);
            }
            return true;
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
            return true;
        }
        return false;
    };

    const handleInputLogic = (value: string) => {
        // Emit typing event (debounced)
        if (value.trim()) {
            const { id, type } = getContainerInfo();
            if (type === 'channel' || type === 'dm' || type === 'topic') {
                sendTyping(id, type);
                // Clear previous stop-typing timeout
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => {
                    stopTyping(id, type);
                }, 3000);
            }
        }

        // Check for mention trigger
        const cursor = editor?.state.selection.$head.pos;
        const textBeforeCursor = cursor
            ? editor?.state.doc.textBetween(0, cursor, '\n', '\n') || value
            : value;
        const match = textBeforeCursor.match(/(?:^|\s)@([^\s@]*)$/);

        if (match) {
            setShowMentions(true);
            setMentionQuery(match[1]);
            setMentionIndex(0);
        } else {
            setShowMentions(false);
        }
    };

    const selectMention = (user: any) => {
        if (!editor) return;
        const cursor = editor.state.selection.$head.pos;
        const textBeforeCursor = editor.state.doc.textBetween(0, cursor, '\n', '\n');
        const match = textBeforeCursor.match(/@([^\s@]*)$/);
        if (!match) return;

        editor.chain().focus().insertContentAt(
            { from: cursor - match[0].length, to: cursor },
            `@${user.name} `
        ).run();
        setShowMentions(false);
    };

    const toggleEncryption = () => {
        // Only allow toggle if not forced or disabled
        if (!isForced && !isDisabled) {
            setIsEncrypted(!isEncrypted);
        }
    };

    const handlePaste = async (e: any) => {
        if (!e.clipboardData) return;
        const items: DataTransferItem[] = Array.from(e.clipboardData.items);
        const imageItem = items.find((item: DataTransferItem) => item.type.startsWith('image/'));
        if (!imageItem) return; // No image—let normal paste proceed
        e.preventDefault();
        const blob = imageItem.getAsFile();
        if (!blob) return;
        const ext = imageItem.type.split('/')[1] || 'png';
        const fileName = `paste-${Date.now()}.${ext}`;
        const file = new File([blob], fileName, { type: imageItem.type });
        setIsUploading(true);
        try {
            const uploaded = await uploadFile(file);
            setAttachments((prev: any) => [...prev, uploaded]);
        } catch (err) {
            console.error('Paste upload failed:', err);
        } finally {
            setIsUploading(false);
        }
    };


    return (
        <div
            className="message-composer"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {/* Drag-and-drop overlay */}
            {isDragging && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 50,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        background: 'rgba(var(--accent-primary-rgb, 99,102,241), 0.10)',
                        border: '2px dashed var(--accent-primary, #6366f1)',
                        borderRadius: 12,
                        backdropFilter: 'blur(2px)',
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: 'var(--accent-primary, #6366f1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 24px rgba(99,102,241,0.35)',
                    }}>
                        <ImageIcon size={26} color="white" />
                    </div>
                    <span style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: 'var(--accent-primary, #6366f1)',
                        letterSpacing: '-0.01em',
                    }}>Drop files to attach</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Images, documents, and more</span>
                </div>
            )}
            <form onSubmit={handleSubmit} className={`composer-container ${effectiveEncryption ? 'encrypted-mode' : ''}`} style={effectiveEncryption ? { borderColor: '#2eb67d', background: 'rgba(46, 182, 125, 0.05)' } : {}}>
                {/* Toolbar */}
                <div className="composer-toolbar">
                    <button type="button" className="composer-toolbar-btn" title="Bold (Ctrl+B)" onClick={formatBold}>
                        <BoldIcon size={16} strokeWidth={2} />
                    </button>
                    <button type="button" className="composer-toolbar-btn" title="Italic (Ctrl+I)" onClick={formatItalic}>
                        <ItalicIcon size={16} strokeWidth={2} />
                    </button>
                    <button type="button" className="composer-toolbar-btn" title="Underline (Ctrl+U)" onClick={formatUnderline}>
                        <UnderlineIcon size={16} strokeWidth={2} />
                    </button>
                    <div style={{ width: 1, height: 16, background: 'var(--border-color)', margin: '0 4px' }} />
                    <button type="button" className="composer-toolbar-btn" title="Link" onClick={formatLink}>
                        <LinkIcon size={16} strokeWidth={2} />
                    </button>
                    <div style={{ width: 1, height: 16, background: 'var(--border-color)', margin: '0 4px' }} />
                    <button type="button" className="composer-toolbar-btn" title="Bulleted list" onClick={formatBulletList}>
                        <List size={16} strokeWidth={2} />
                    </button>
                    <button type="button" className="composer-toolbar-btn" title="Numbered list" onClick={formatNumberList}>
                        <ListOrdered size={16} strokeWidth={2} />
                    </button>
                    <div style={{ width: 1, height: 16, background: 'var(--border-color)', margin: '0 4px' }} />
                    <button type="button" className="composer-toolbar-btn" title="Code" onClick={formatCode}>
                        <CodeIcon size={16} strokeWidth={2} />
                    </button>
                    <button type="button" className="composer-toolbar-btn" title="Quote" onClick={formatQuote}>
                        <QuoteIcon size={16} strokeWidth={2} />
                    </button>

                    {(effectiveEncryption && canEncrypt) && (
                        <>
                            <div style={{ width: 1, height: 16, background: 'var(--border-color)', margin: '0 4px' }} />
                            <button
                                type="button"
                                className={isThread ? `composer-toolbar-btn ${selfDestruct ? 'active-danger' : ''}` : `btn btn-sm ${selfDestruct ? 'btn-danger' : 'btn-ghost'}`}
                                onClick={() => setSelfDestruct(!selfDestruct)}
                                title="Self Destruct"
                                style={!isThread ? (selfDestruct ? {} : { color: 'var(--text-muted)' }) : {}}
                            >
                                <Bomb size={16} strokeWidth={2} />
                                {selfDestruct && !isThread && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600 }}>AUTO-DELETE</span>}
                            </button>
                        </>
                    )}

                    {canEncrypt && (
                        <>
                            {!(effectiveEncryption && canEncrypt) && <div style={{ width: 1, height: 16, background: 'var(--border-color)', margin: '0 4px' }} />}
                            <button
                                type="button"
                                className={isThread ? `composer-toolbar-btn ${effectiveEncryption ? 'active-secure' : ''}` : `btn btn-sm ${effectiveEncryption ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={toggleEncryption}
                                title={isForced ? 'Encryption is required' : isDisabled ? 'Encryption is disabled' : 'Toggle Encryption'}
                                style={!isThread ? (effectiveEncryption ? { background: '#2eb67d', borderColor: '#2eb67d' } : { color: '#2eb67d' }) : {}}
                                disabled={isForced || isDisabled}
                            >
                                {effectiveEncryption ? <Lock size={16} strokeWidth={2} style={!isThread ? { marginRight: 6 } : {}} /> : <Unlock size={16} strokeWidth={2} style={!isThread ? { marginRight: 6 } : {}} />}
                                {!isThread && (effectiveEncryption ? 'Secure' : 'Plain')}
                            </button>
                        </>
                    )}
                </div>

                {/* Input */}
                <div style={{ position: 'relative' }}>
                    {showMentions && (
                        <div className="mentions-popup">
                            {users.filter(u => u.name.toLowerCase().includes(mentionQuery.toLowerCase())).map((user, index) => (
                                <div
                                    key={user.id}
                                    className={`mention-item ${index === mentionIndex ? 'selected' : ''}`}
                                    onClick={() => selectMention(user)}
                                >
                                    <div className="avatar avatar-xs" style={{ width: 20, height: 20, fontSize: 10, background: 'var(--bg-tertiary)' }}>
                                        {user.name.charAt(0)}
                                    </div>
                                    <span style={{ fontSize: 13 }}>{user.name}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="composer-editor-wrapper" onClick={() => editor?.commands.focus()}>
                        <EditorContent editor={editor} disabled={!isOnline} />
                    </div>
                </div>

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                    <div className="attachments-preview">
                        {attachments.map((file: any, i) => {
                            const thresholdBytes = (settings?.file_size_threshold_mb || 5) * 1024 * 1024;
                            const sizeBytes = file.size_bytes !== undefined ? file.size_bytes : file.size;
                            const isSmall = sizeBytes < thresholdBytes;
                            const showRetention = settings?.file_retention_enabled && settings?.show_retention_warning && isSmall;
                            const fileName = file.original_name || file.name || 'Unknown File';
                            const mimeType = file.mime_type || file.type || '';
                            let fileUrl = file.url || '';
                            if (fileUrl && authToken) {
                                if (fileUrl.includes('/api/files/') || fileUrl.startsWith('/api/')) {
                                    fileUrl = `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(authToken)}`;
                                }
                            }
                            fileUrl = getAbsoluteUrl(fileUrl);

                            return (
                                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <div className="attachment-chip" style={mimeType.startsWith('image/') && fileUrl ? { padding: '4px', border: 'none', background: 'transparent' } : {}}>
                                        {mimeType.startsWith('image/') && fileUrl ? (
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <img
                                                    src={fileUrl}
                                                    alt={fileName}
                                                    style={{ height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'block' }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeAttachment(i)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: -6,
                                                        right: -6,
                                                        background: 'var(--bg-primary)',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: '50%',
                                                        width: 20,
                                                        height: 20,
                                                        padding: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        zIndex: 10,
                                                        color: 'var(--text-muted)'
                                                    }}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <FileIcon size={14} />
                                                <span className="truncate" style={{ maxWidth: 100 }}>
                                                    {fileName}
                                                </span>
                                            </>
                                        )}
                                        {(!mimeType.startsWith('image/') || !fileUrl) && (
                                            <button
                                                type="button"
                                                onClick={() => removeAttachment(i)}
                                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' }}
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                    {showRetention && !file.deleted_at && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            fontSize: 10,
                                            color: 'var(--text-muted)',
                                            paddingLeft: 4
                                        }}>
                                            <span style={{ fontSize: 10 }}>
                                                {file.never_delete ? 'Will not be deleted' : `Might be deleted in ${file.retention_days || settings?.small_file_retention_days || 30} days`}
                                            </span>
                                            {settings?.allow_user_never_delete && (
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        id={`never-delete-${i}`}
                                                        checked={!!file.never_delete}
                                                        onChange={(e) => handleRetentionUpdate(file.id || (file as any).file_id, e.target.checked)}
                                                        style={{ width: 12, height: 12, cursor: 'pointer' }}
                                                    />
                                                    <span>Never delete</span>
                                                </label>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Footer */}
                <div className="composer-footer">
                    <div className="composer-footer-actions">
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                        <button type="button" className="composer-toolbar-btn" title="Attach file" onClick={() => fileInputRef.current?.click()} disabled={!isOnline}>
                            <Paperclip size={18} strokeWidth={1.5} />
                        </button>
                        {!settings?.disable_emojis && (
                            <div style={{ position: 'relative' }} ref={emojiRef}>
                                <button
                                    type="button"
                                    className={`composer-toolbar-btn ${showEmoji ? 'active' : ''}`}
                                    title="Emoji"
                                    onClick={() => setShowEmoji(!showEmoji)}
                                >
                                    <Smile size={18} strokeWidth={1.5} />
                                </button>
                                {showEmoji && (
                                    <div className="composer-popover">
                                        <div className="emoji-grid">
                                            {['👍', '👎', '✅', '🔥', '❤️', '😂'].map(emoji => (
                                                <button
                                                    key={emoji}
                                                    type="button"
                                                    className="emoji-btn"
                                                    onClick={() => addEmoji(emoji)}
                                                >
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!isThread && (
                            <div style={{ position: 'relative' }} ref={shortcutsRef}>
                                <button
                                    type="button"
                                    className={`composer-toolbar-btn ${showShortcuts ? 'active' : ''}`}
                                    title="Keyboard Shortcuts"
                                    onClick={() => setShowShortcuts(!showShortcuts)}
                                >
                                    <Keyboard size={18} strokeWidth={1.5} />
                                </button>
                                {showShortcuts && (
                                    <div className="composer-popover" style={{ width: 220, padding: 12 }}>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: 13, borderBottom: '1px solid var(--border-color)', paddingBottom: 6 }}>
                                            Keyboard Shortcuts
                                        </h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Bold</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>Ctrl+B</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Italic</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>Ctrl+I</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Underline</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>Ctrl+U</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Link</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>Ctrl+K</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Edit Last</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>↑ (Arrow Up)</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>New Line</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>Shift+Enter</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Send</span>
                                                <kbd style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>Enter</kbd>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {isThread && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer', marginLeft: 4 }}>
                                <input
                                    type="checkbox"
                                    checked={isBroadcast}
                                    onChange={(e) => setIsBroadcast(e.target.checked)}
                                    style={{ width: 12, height: 12, margin: 0 }}
                                />
                                Also send to chat
                            </label>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }} ref={scheduleRef}>
                        {onCancel && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={onCancel}
                            >
                                Cancel
                            </button>
                        )}
                        {isSendingDelayed && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={cancelDelayedSend}
                                style={{ color: 'var(--color-error)' }}
                            >
                                Undo
                            </button>
                        )}
                        <button
                            type="submit"
                            className="send-btn"
                            disabled={!content.trim() && attachments.length === 0 || isUploading || !isOnline || isSendingDelayed}
                            style={{
                                ...(effectiveEncryption ? { background: '#2eb67d' } : {}),
                                ...(submitLabel ? {
                                    width: 'auto',
                                    padding: '0 16px',
                                    height: 32,
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: 600
                                } : {})
                            }}
                        >
                            {isUploading ? <Loader2 size={18} className="spin" /> : (
                                isSendingDelayed ? <Loader2 size={18} className="spin" /> : (
                                    submitLabel ? submitLabel : (scheduledTime ? <Clock size={18} strokeWidth={2} /> : <Send size={18} strokeWidth={2} />)
                                )
                            )}
                        </button>
                        {!isEditing && (
                            <button
                                type="button"
                                className="composer-toolbar-btn"
                                onClick={() => setShowSchedule(!showSchedule)}
                                title="Schedule send"
                            >
                                <ChevronDown size={14} strokeWidth={2} />
                            </button>
                        )}
                        {showSchedule && !isEditing && (
                            <div className="schedule-popup">
                                <div style={{ padding: '4px 8px', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)', marginBottom: 4 }}>
                                    Schedule for later
                                </div>
                                {scheduledTime && (
                                    <button
                                        type="button"
                                        className="schedule-option"
                                        onClick={() => { setScheduledTime(null); setShowSchedule(false); }}
                                        style={{ color: 'var(--accent-danger)' }}
                                    >
                                        <X size={14} /> Clear scheduled time
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="schedule-option"
                                    onClick={() => {
                                        const inOneHour = new Date(Date.now() + 60 * 60 * 1000).toISOString();
                                        setScheduledTime(inOneHour);
                                        setShowSchedule(false);
                                    }}
                                >
                                    <Clock size={14} /> In 1 hour
                                </button>
                                <button
                                    type="button"
                                    className="schedule-option"
                                    onClick={() => {
                                        const tomorrow9AM = new Date();
                                        tomorrow9AM.setDate(tomorrow9AM.getDate() + 1);
                                        tomorrow9AM.setHours(9, 0, 0, 0);
                                        setScheduledTime(tomorrow9AM.toISOString());
                                        setShowSchedule(false);
                                    }}
                                >
                                    <Sun size={14} /> Tomorrow 9:00 AM
                                </button>
                                <button
                                    type="button"
                                    className="schedule-option"
                                    onClick={() => {
                                        const monday9AM = new Date();
                                        monday9AM.setDate(monday9AM.getDate() + (8 - monday9AM.getDay()) % 7);
                                        monday9AM.setHours(9, 0, 0, 0);
                                        setScheduledTime(monday9AM.toISOString());
                                        setShowSchedule(false);
                                    }}
                                >
                                    <Calendar size={14} /> Monday 9:00 AM
                                </button>
                                <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 4, paddingTop: 8 }}>
                                    <label style={{ display: 'block', padding: '4px 8px', fontSize: '12px', color: 'var(--text-muted)', marginBottom: 4 }}>
                                        Custom time:
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={customDateValue}
                                        min={(() => {
                                            const now = new Date();
                                            now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                                            return now.toISOString().slice(0, 16);
                                        })()}
                                        onChange={(e) => setCustomDateValue(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '6px 8px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 4,
                                            background: 'var(--bg-secondary)',
                                            fontSize: '13px',
                                            color: 'var(--text-primary)',
                                            marginBottom: 8,
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={!customDateValue}
                                        onClick={() => {
                                            if (customDateValue) {
                                                setScheduledTime(new Date(customDateValue).toISOString());
                                                setShowSchedule(false);
                                            }
                                        }}
                                        style={{ width: '100%', fontSize: 13, padding: '6px 12px' }}
                                    >
                                        Confirm
                                    </button>
                                </div>
                                {scheduledTime && (
                                    <div style={{ padding: '8px', fontSize: '11px', color: 'var(--accent-primary)', background: 'rgba(29, 155, 209, 0.1)', borderRadius: 4, marginTop: 4 }}>
                                        Scheduled: {new Date(scheduledTime).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </form>

            {/* Application Retention Warning Modal */}
            {pendingFiles.length > 0 && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 20
                }}>
                    <div style={{
                        backgroundColor: 'var(--bg-primary)',
                        borderRadius: 12,
                        width: '100%',
                        maxWidth: 450,
                        overflow: 'hidden',
                        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)' }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Clock size={20} className="text-accent-primary" />
                                File Retention Policy
                            </h3>
                        </div>

                        <div style={{ padding: '24px' }}>
                            <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                Validating {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''} against retention policy.
                            </p>

                            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, marginBottom: 20 }}>
                                {pendingFiles.map((file, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '4px 0' }}>
                                        {file.type.startsWith('image/') ? <ImageIcon size={16} /> : <FileIcon size={16} />}
                                        <span className="truncate" style={{ flex: 1 }}>{file.name}</span>
                                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                            {(file.size / (1024 * 1024)).toFixed(1)} MB
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* Large Files: Select Retention */}
                                {pendingFiles.some(f => f.size >= (settings?.file_size_threshold_mb || 5) * 1024 * 1024) && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                                            Retention Period
                                        </label>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {(settings?.large_file_retention_options || [30, 90, 180, 365, 0]).map((days: number) => (
                                                <button
                                                    key={days}
                                                    type="button"
                                                    onClick={() => {
                                                        setRetentionDays(days);
                                                        if (days !== 0) setNeverDelete(false);
                                                    }}
                                                    style={{
                                                        padding: '6px 12px',
                                                        borderRadius: 6,
                                                        border: `1px solid ${retentionDays === days ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                                                        background: retentionDays === days ? 'var(--accent-primary-dim)' : 'transparent',
                                                        color: retentionDays === days ? 'var(--accent-primary)' : 'var(--text-primary)',
                                                        fontSize: 13,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {days === 0 ? 'Never' : `${days} days`}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Small Files: Never Delete Option */}
                                {pendingFiles.every(f => f.size < (settings?.file_size_threshold_mb || 5) * 1024 * 1024) && settings?.allow_user_never_delete && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={neverDelete}
                                            onChange={(e) => {
                                                setNeverDelete(e.target.checked);
                                                if (e.target.checked) setRetentionDays(0);
                                                else setRetentionDays(settings?.small_file_retention_days || 30);
                                            }}
                                        />
                                        <span style={{ fontSize: 13 }}>Inhibit automatic deletion</span>
                                    </label>
                                )}

                                {/* Compression Toggle */}
                                {pendingFiles.some(f => f.type.startsWith('image/')) && settings?.enable_image_compression && settings?.allow_user_disable_compression && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={disableCompression}
                                            onChange={(e) => setDisableCompression(e.target.checked)}
                                        />
                                        <span style={{ fontSize: 13 }}>Disable image compression (Keep original quality)</span>
                                    </label>
                                )}

                                <div style={{
                                    padding: '12px',
                                    background: 'rgba(234, 179, 8, 0.1)',
                                    border: '1px solid rgba(234, 179, 8, 0.2)',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    color: 'var(--text-primary)',
                                    display: 'flex',
                                    gap: 10
                                }}>
                                    <Calendar size={16} className="text-yellow-500 shrink-0" />
                                    <div>
                                        File will be deleted on: <strong>
                                            {retentionDays === 0 || neverDelete
                                                ? 'Never'
                                                : new Date(Date.now() + (retentionDays || 30) * 24 * 60 * 60 * 1000 + (settings?.enable_grace_period ? (settings.retention_grace_period_hours || 48) * 60 * 60 * 1000 : 0)).toLocaleDateString()
                                            }
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '16px 24px', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button
                                type="button"
                                onClick={cancelUpload}
                                className="btn btn-ghost"
                            >
                                Cancel Upload
                            </button>
                            <button
                                type="button"
                                onClick={confirmUpload}
                                className="btn btn-primary"
                                disabled={isUploading}
                            >
                                {isUploading ? <Loader2 size={16} className="spin" /> : 'Confirm Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SensitiveInfoModal
                isOpen={sensitiveModalState.isOpen}
                detectedType={sensitiveModalState.detectedType}
                isCurrentlyEncrypted={effectiveEncryption}
                isCurrentlyExpiring={selfDestruct}
                onConfirm={(encryptParam, selfDestructParam) => {
                    setSensitiveModalState({ isOpen: false, detectedType: null });
                    // Don't set isEncrypted(true) here as we want it to reset after send anyway
                    // if it's an optional view. startDelayedSend will handle the reset.
                    setSelfDestruct(selfDestructParam);
                    startDelayedSend(encryptParam, selfDestructParam);
                }}
                onDecline={() => {
                    setSensitiveModalState({ isOpen: false, detectedType: null });
                    startDelayedSend(effectiveEncryption, selfDestruct);
                }}
                onCancel={() => {
                    setSensitiveModalState({ isOpen: false, detectedType: null });
                }}
            />

        </div>
    );
}
